/**
 * Утилиты для оптимизации работы со слотами
 */

interface Slot {
  start: number;
  end: number;
  date: string | Date;
}

interface SlotCache {
  cacheKey: string;
  busyHours: Set<number>;
}

// Кэш для уже вычисленных занятых часов
let slotCache: SlotCache | null = null;

/**
 * Очистка кэша слотов
 */
export const clearSlotCache = () => {
  slotCache = null;
};

/**
 * Получить занятые часы для конкретной даты с кэшированием
 */
export const getBusyHoursForDate = (
  date: Date,
  bookedSlots: Slot[],
  tmpBusySlots: Slot[],
  excludeSlot?: { start: number; end: number }
): Set<number> => {
  const dateKey = date.toISOString().slice(0, 10);
  const cacheKey = `${dateKey}:${excludeSlot?.start ?? ''}:${excludeSlot?.end ?? ''}`;

  if (slotCache && slotCache.cacheKey === cacheKey) {
    return slotCache.busyHours;
  }

  // Вычисляем занятые часы
  const busyHours = new Set<number>();
  
  [...bookedSlots, ...tmpBusySlots]
    .filter(s => new Date(s.date).toISOString().slice(0, 10) === dateKey)
    .forEach(s => {
      // Пропускаем исключенный слот
      if (excludeSlot && s.start === excludeSlot.start && s.end === excludeSlot.end) {
        return;
      }
      
      for (let h = s.start; h < s.end; h++) {
        busyHours.add(h);
      }
    });

  slotCache = { cacheKey, busyHours };

  return busyHours;
};

/**
 * Проверка, занят ли интервал времени
 * Оптимизированная версия с использованием кэша
 */
export const isBusyInterval = (
  timeInterval: string,
  dateObj: Date,
  bookedSlots: Slot[],
  tmpBusySlots: Slot[],
  mode: 'new' | 'edit' = 'new',
  selectedHours?: Slot[]
): boolean => {
  const m = timeInterval.match(/^(\d{1,2})(?:\s*(?:am|pm))?\s*-\s*(\d{1,2})(?:\s*(?:am|pm))?$/i);
  if (!m) return true;
  
  const startHour = parseInt(m[1], 10);
  let rawEnd = parseInt(m[2], 10);
  if (rawEnd === 0) rawEnd = 24;

  const excludeSlot = mode === 'edit' && selectedHours && selectedHours.length === 1
    ? selectedHours[0]
    : undefined;

  const busyHours = getBusyHoursForDate(dateObj, bookedSlots, tmpBusySlots, excludeSlot);

  // В режиме переноса проверяем весь блок брони (startHour … startHour + duration)
  const hoursToCheck = mode === 'edit' && selectedHours && selectedHours.length > 0
    ? selectedHours[0].end - selectedHours[0].start
    : rawEnd - startHour;

  for (let h = startHour; h < startHour + hoursToCheck; h++) {
    if (busyHours.has(h)) return true;
  }

  return false;
};

/**
 * Можно ли начать бронь/перенос с startHour на длительность durationHours
 * (занятость + рабочие часы комнаты). Используется при клике в режиме edit.
 */
export const isEditSlotBlocked = (
  startHour: number,
  dateObj: Date,
  durationHours: number,
  bookedSlots: Slot[],
  tmpBusySlots: Slot[],
  excludeSlot: { start: number; end: number },
  roomSchedule?: any
): boolean => {
  const dateKey = dateObj.toISOString().slice(0, 10);
  const busyHours = getBusyHoursForDate(dateObj, bookedSlots, tmpBusySlots, excludeSlot);

  for (let h = startHour; h < startHour + durationHours; h++) {
    if (busyHours.has(h)) return true;
  }

  const limitation = roomSchedule?.["1"]?.limitation;
  if (limitation) {
    const { start, end } = limitation;
    if (startHour < start || startHour + durationHours > end) {
      return true;
    }
  }

  return false;
};

/**
 * Дебаунс функция для ограничения частоты вызовов
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle функция для ограничения частоты вызовов
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};
